import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { timeOffAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Calendar, Plus, Loader2, Check, X, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export default function TimeOffRequests() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const defaultForm = {
    start_date: '',
    end_date: '',
    leave_type: '',
    hold_phone_calls: '',
    reason: '',
    employee_signature: '',
    confirm: false,
  };
  const [form, setForm] = useState(defaultForm);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await timeOffAPI.getAll();
      setRequests(res.data);
    } catch (error) {
      console.error('Failed to fetch time off requests:', error);
      toast.error('Failed to load time off requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) {
      toast.error('Please select both dates');
      return;
    }
    if (!form.leave_type) {
      toast.error('Please select a type of absence');
      return;
    }
    if (form.hold_phone_calls === '') {
      toast.error('Please indicate whether to hold phone calls');
      return;
    }
    if (!form.employee_signature.trim()) {
      toast.error('Please type your full name as signature');
      return;
    }
    if (!form.confirm) {
      toast.error('Please confirm the information is accurate');
      return;
    }
    setSaving(true);
    try {
      await timeOffAPI.request({
        start_date: form.start_date,
        end_date: form.end_date,
        leave_type: form.leave_type,
        hold_phone_calls: form.hold_phone_calls === 'yes',
        reason: form.reason,
        employee_signature: form.employee_signature.trim(),
      });
      toast.success('Time off request submitted — admins have been notified.');
      setShowForm(false);
      setForm({ ...defaultForm, employee_signature: user?.name || '' });
      fetchRequests();
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSaving(false);
    }
  };

  // Reject-with-reason modal state
  const [rejectTarget, setRejectTarget] = useState(null); // { id, employee_name, start_date, end_date }
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Delete confirm modal state (admin only)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleApprove = async (id) => {
    try {
      await timeOffAPI.approve(id);
      toast.success('Request approved');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await timeOffAPI.reject(rejectTarget.id, rejectReason.trim());
      toast.success('Request rejected — employee has been emailed.');
      setRejectTarget(null);
      setRejectReason('');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to reject request');
    } finally {
      setRejecting(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await timeOffAPI.delete(deleteTarget.id);
      toast.success('Request deleted. Employee may now submit a revised request.');
      setDeleteTarget(null);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to delete request');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="time-off-requests-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Time Off Requests</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? 'Review and manage employee time off requests' : 'Request time off and track your submissions'}
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="request-time-off-btn">
          <Plus className="w-4 h-4 mr-2" />
          Request Time Off
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Calendar className="w-5 h-5 text-muted-foreground" />
            {isAdmin ? 'All Time Off Requests' : 'Your Time Off Requests'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {requests.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-muted-foreground">No time off requests</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead>Employee</TableHead>}
                  <TableHead>Dates</TableHead>
                  <TableHead className="hidden md:table-cell">Reason</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin && <TableHead className="w-32">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id} data-testid={`timeoff-row-${request.id}`}>
                    {isAdmin && (
                      <TableCell className="font-medium">{request.user_name}</TableCell>
                    )}
                    <TableCell>
                      <div>
                        <p>{format(parseISO(request.start_date), 'MMM d, yyyy')}</p>
                        <p className="text-sm text-muted-foreground">
                          to {format(parseISO(request.end_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {request.reason || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={`badge-status ${getStatusBadge(request.status)}`}>
                        {request.status}
                      </Badge>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        {request.status === 'pending' ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 hover:text-emerald-700"
                              onClick={() => handleApprove(request.id)}
                              data-testid={`approve-${request.id}`}
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => {
                                setRejectTarget({
                                  id: request.id,
                                  employee_name: request.user_name,
                                  start_date: request.start_date,
                                  end_date: request.end_date,
                                });
                                setRejectReason('');
                              }}
                              data-testid={`reject-${request.id}`}
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setDeleteTarget({
                              id: request.id,
                              status: request.status,
                              employee_name: request.user_name,
                              start_date: request.start_date,
                              end_date: request.end_date,
                            })}
                            data-testid={`delete-${request.id}`}
                            title={`Delete ${request.status} request (employee may then resubmit)`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={(open) => {
        setShowForm(open);
        if (open) {
          setForm((prev) => ({ ...prev, employee_signature: user?.name || '' }));
        }
      }}>
        <DialogContent
          data-testid="timeoff-form-dialog"
          className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">Request Time Off</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Type of absence */}
            <div className="space-y-2">
              <Label>Type of Absence *</Label>
              <div className="grid grid-cols-3 gap-2" data-testid="leave-type-group">
                {[
                  { value: 'personal', label: 'Personal Leave' },
                  { value: 'vacation', label: 'Vacation' },
                  { value: 'other', label: 'Other' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors ${
                      form.leave_type === opt.value ? 'border-primary bg-primary/10' : 'border-muted'
                    }`}
                  >
                    <input
                      type="radio"
                      name="leave_type"
                      value={opt.value}
                      checked={form.leave_type === opt.value}
                      onChange={(e) => setForm((p) => ({ ...p, leave_type: e.target.value }))}
                      data-testid={`leave-type-${opt.value}`}
                    />
                    <span className="text-sm font-medium">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                  data-testid="timeoff-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date *</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                  required
                  data-testid="timeoff-end-date"
                />
              </div>
            </div>

            {/* Brief explanation */}
            <div className="space-y-2">
              <Label htmlFor="reason">Brief Explanation</Label>
              <Textarea
                id="reason"
                value={form.reason}
                onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Reason for time off"
                rows={3}
                data-testid="timeoff-reason"
              />
            </div>

            {/* Hold phone calls */}
            <div className="space-y-2">
              <Label>Hold Phone Calls *</Label>
              <div className="flex gap-3" data-testid="hold-calls-group">
                {[
                  { value: 'yes', label: 'Yes — please hold my calls' },
                  { value: 'no', label: 'No — I am reachable' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors flex-1 ${
                      form.hold_phone_calls === opt.value ? 'border-primary bg-primary/10' : 'border-muted'
                    }`}
                  >
                    <input
                      type="radio"
                      name="hold_phone_calls"
                      value={opt.value}
                      checked={form.hold_phone_calls === opt.value}
                      onChange={(e) => setForm((p) => ({ ...p, hold_phone_calls: e.target.value }))}
                      data-testid={`hold-calls-${opt.value}`}
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Digital signature */}
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="employee_signature">Employee Signature (type full name) *</Label>
              <Input
                id="employee_signature"
                value={form.employee_signature}
                onChange={(e) => setForm((p) => ({ ...p, employee_signature: e.target.value }))}
                placeholder="Your full name"
                required
                data-testid="timeoff-signature"
                className="font-serif italic text-lg"
              />
              <label className="flex items-start gap-2 text-sm text-muted-foreground pt-1">
                <input
                  type="checkbox"
                  checked={form.confirm}
                  onChange={(e) => setForm((p) => ({ ...p, confirm: e.target.checked }))}
                  className="mt-1"
                  data-testid="timeoff-confirm"
                />
                <span>
                  I certify that the above information is accurate. Typing my name acts as my
                  electronic signature on this Request For Time Off form.
                </span>
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="submit-timeoff-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject with reason dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <DialogContent
          data-testid="reject-dialog"
          className="w-[95vw] sm:max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Reject Time Off Request</DialogTitle>
          </DialogHeader>
          {rejectTarget && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted p-3 text-sm">
                <p><strong>{rejectTarget.employee_name}</strong></p>
                <p className="text-muted-foreground">
                  {rejectTarget.start_date} → {rejectTarget.end_date}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reject_reason">Reason (optional — included in the rejection email)</Label>
                <Textarea
                  id="reject_reason"
                  rows={4}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Example: Requested dates conflict with a customer booking. Please pick alternate days."
                  data-testid="reject-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} disabled={rejecting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitReject}
              disabled={rejecting}
              data-testid="reject-confirm-btn"
            >
              {rejecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Rejecting...
                </>
              ) : (
                'Reject & Email Employee'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete approved/rejected confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent
          data-testid="delete-dialog"
          className="w-[95vw] sm:max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Delete Request</DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-3">
              <p className="text-sm">
                Delete this <strong>{deleteTarget.status}</strong> request for{' '}
                <strong>{deleteTarget.employee_name}</strong> ({deleteTarget.start_date} → {deleteTarget.end_date})?
              </p>
              <p className="text-sm text-muted-foreground">
                The employee will be able to submit a revised request afterwards. This action cannot be undone.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitDelete}
              disabled={deleting}
              data-testid="delete-confirm-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
